import { generateTrigrams, calculateAsymTrigramMatching } from './shared-util';

// Lexical exercise-name matcher, extracted from the exercise picker's search
// (`exercise-switch.ts` searchAsync_internal) so the scoring lives in one shared,
// unit-tested place. Its one consumer is the exercise picker's live search, which
// scores the exercise NAME only — the same behavior as before the extraction.
//
// The scoring formula is unchanged from the picker: a candidate text scores
// `(trigramMatch + wordOverlap) / 2`, and is rejected outright when its trigram
// match is <= 0.2 and the search is not a plain substring of it.

// a search text prepared once, reused against many candidates
export interface LexicalQuery {
  trigrams: Set<string>;
  lowercase: string;
  words: string[];
}

// one candidate text (an exercise name) prepared once at load time
interface PreparedText {
  trigrams: Set<string>;
  lowercase: string;
  words: string[];
}

// a candidate exercise with its prepared name
export interface LexicalCandidate {
  name: PreparedText;
}

// candidates whose trigram match is at or below this are rejected unless the
// search text is a plain substring of the candidate (picker-original cutoff)
const TRIGRAM_CUTOFF = 0.2;

function prepareText(text: string): PreparedText {
  const lowercase = text.toLocaleLowerCase();
  return {
    trigrams: generateTrigrams(text),
    lowercase,
    words: splitWords(lowercase),
  };
}

function splitWords(lowercase: string): string[] {
  return lowercase.replace(/[^a-z0-9]/g, ' ').split(/\s+/).filter(w => w);
}

/** Prepare a search text once before scoring it against many candidates. */
export function prepareLexicalQuery(searchText: string): LexicalQuery {
  return prepareText(searchText.trim());
}

/** Prepare a candidate exercise once (at load time) for repeated scoring. */
export function prepareLexicalCandidate(name: string): LexicalCandidate {
  return { name: prepareText(name) };
}

/** Score a candidate's name against the query. Returns undefined when the
 *  candidate fails the trigram/substring cutoff (i.e. should be excluded). */
export function scoreLexicalMatch(query: LexicalQuery, candidate: LexicalCandidate): number | undefined {
  const text = candidate.name;
  const triMatch = calculateAsymTrigramMatching(query.trigrams, text.trigrams);
  const includes = text.lowercase.includes(query.lowercase);
  if (triMatch <= TRIGRAM_CUTOFF && !includes) return undefined;

  // word-level matching bonus (same logic as food search)
  let sameWords = 0;
  for (const word of query.words) {
    if (text.words.includes(word)) {
      sameWords++;
    }
  }
  const wordsMatch = (sameWords * 2) / (query.words.length + text.words.length);
  return (triMatch + wordsMatch) / 2;
}
